import { usePersistedFilters } from '~/hooks/usePersistedFilters'
import {
  type SpotPriceSectionId,
  deserializeSpotPriceSections,
  serializeSpotPriceSections,
} from '~/pages/SpotPrice/models/persistedSections'

/**
 * スポット価格のアコーディオン開閉保持 key。
 *
 * フィルタ (`SPOT_PRICE_FILTERS_STORAGE_KEY`) とは別 key にする。概念が違い、
 * 書き戻しの契機も独立しているため (`models/persistedSections.ts` の docstring)。
 */
export const SPOT_PRICE_SECTIONS_STORAGE_KEY = 'ebalance-spot-price-sections'

type Result = {
  /** 開いているセクション。`AccordionRoot` の `value` にそのまま渡す。 */
  openSections: SpotPriceSectionId[]
  /** `AccordionRoot` の `onValueChange` から受けた次の状態で置き換える。 */
  setOpenSections: (next: SpotPriceSectionId[]) => void
}

/**
 * アコーディオンの開閉状態を localStorage に保持し、他画面への遷移後・再訪時に
 * 復元する hook。
 *
 * この画面はアコーディオン見出しから供給力変化画面へ route 遷移するため、
 * 保持しないと**戻るたびに開き直す**ことになる (2026-09-04 デザイナー要望
 * 「ユーザーが後から開いた状態は、本画面から離れたあとに戻ってきても維持する」)。
 *
 * 直列化・復元・既定値・検証は `models/persistedSections.ts` の純関数が SoT。
 * 読み書きのタイミング (初期復元 / 変更時の書き戻し / 未操作の既定値は書かない /
 * Strict Mode 対策) は共通 hook `usePersistedFilters` が持つ。
 *
 * 共通 hook は「フィルタ」の名前だが中身は汎用の
 * 「localStorage 保持 state」で、 保持対象を限定していない
 * (`storageKey` / `codec` を受けるだけ)。
 */
export function usePersistedSpotPriceSections(): Result {
  const { filters, updateFilters } = usePersistedFilters<SpotPriceSectionId[]>({
    storageKey: SPOT_PRICE_SECTIONS_STORAGE_KEY,
    codec: {
      deserialize: deserializeSpotPriceSections,
      serialize: serializeSpotPriceSections,
    },
  })

  return {
    openSections: filters,
    setOpenSections: (next) => updateFilters(() => next),
  }
}
